import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  FlatList,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { router } from "expo-router";
import { doc, onSnapshot } from "firebase/firestore";

import BusinessCardResponsive from "@/components/business/BusinessCardResponsive";
import {
  claimNextPending,
  extendClaim,
  getAdminBusinessCategoryLabel,
  getAdminBusinessSubcategoryLabel,
  listenPendingUnclaimed,
  normalizeAdminBusiness,
  releaseClaim,
  shouldShowAdminOwnerSuggestedLabel,
  triageApprovePublish,
  triageEditMinor,
  triageReject,
  triageRequestChanges,
  type AdminBusiness,
} from "@/lib/admin/businessesAdmin";
import { db } from "@/lib/firebase";
import type { Business } from "@/lib/types";

type NoteMode = null | "reject" | "changes";

type MinorEditDraft = {
  name?: string;
  slug?: string;
  phone?: string;
  website?: string;
  addressLine?: string;
};

type TriageLocation = {
  areaName?: string;
  cityName?: string;
  countyName?: string;
};

type TriageBusiness = MinorEditDraft & {
  id: string;

  businessName?: string;
  ownerId?: string;
  ownerPhone?: string;

  status?: string;
  isPublished?: boolean;
  isDeleted?: boolean;

  logoUrl?: string;
  coverUrl?: string;

  category?: string;
  categories?: unknown;
  categoryStatus?: string;
  categoryName?: string;
  subCategory?: string | { name?: string };
  subCategoryText?: string;
  subCategories?: unknown;
  subcategories?: unknown;
  subcategory?: unknown;
  subcategoryStatus?: string;
  subcategoryName?: string;
  displayCategory?: string;
  displaySubcategory?: string;
  isCategoryOwnerSuggested?: boolean;
  isSubcategoryOwnerSuggested?: boolean;

  countyName?: string;
  cityName?: string;
  areaName?: string;
  location?: TriageLocation;

  images?: unknown[];
  productImages?: unknown[];
  items?: unknown[];

  rejectedReason?: string;
  changesRequestMessage?: string;

  [key: string]: unknown;
};

type QueueBusiness = AdminBusiness & {
  name?: string;
  businessName?: string;
  status?: string;
  ownerId?: string;
  countyName?: string;
  cityName?: string;
  areaName?: string;
  location?: TriageLocation;
};

const CHANGE_TEMPLATES = [
  "Upload clearer logo (square image).",
  "Add at least 2 product/service images.",
  "Fix phone number format (07xx… or +2547xx…).",
  "Choose the correct category/subcategory.",
  "Add a specific area/estate (not only county).",
  "Business name should not include emojis or ALL CAPS.",
];

const REJECT_TEMPLATES = [
  "Spam / unrelated listing",
  "Duplicate business",
  "Offensive / prohibited content",
  "Fake / invalid phone number",
  "Scam / suspicious",
];

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) return error.message;
  return fallback;
};

const hasText = (value: unknown) => {
  return typeof value === "string" && value.trim().length > 0;
};

const toEditValue = (value: unknown) => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return String(value);
};

const PHOTO_VALUE_FIELDS = [
  "imageUrl",
  "imageUri",
  "imageURI",
  "photoUrl",
  "img",
  "image",
  "url",
  "uri",
  "downloadUrl",
  "images",
  "imageUrls",
] as const;

const addPhotoUrls = (value: unknown, urls: Set<string>) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) urls.add(trimmed);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => addPhotoUrls(entry, urls));
    return;
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    PHOTO_VALUE_FIELDS.forEach((field) => addPhotoUrls(record[field], urls));
  }
};

const getBusinessPhotoUrls = (value: TriageBusiness | null | undefined) => {
  const urls = new Set<string>();

  addPhotoUrls(value?.images, urls);
  addPhotoUrls(value?.productImages, urls);

  if (Array.isArray(value?.items)) {
    value.items.forEach((item) => addPhotoUrls(item, urls));
  }

  return Array.from(urls);
};

const getBusinessPhotoCount = (value: TriageBusiness | null | undefined) => {
  return getBusinessPhotoUrls(value).length;
};

export default function AdminModerationTriageScreen() {
  const { width } = useWindowDimensions();
  const compact = width < 1280;

  const [queue, setQueue] = useState<AdminBusiness[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [business, setBusiness] = useState<TriageBusiness | null>(null);
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [busyAction, setBusyAction] = useState<
    null | "approve" | "reject" | "changes" | "edit" | "next"
  >(null);

  const [noteMode, setNoteMode] = useState<NoteMode>(null);
  const [note, setNote] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState<MinorEditDraft>({});

  const unsubRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    const unsub = listenPendingUnclaimed({ limitCount: 80 }, setQueue);
    return () => unsub();
  }, []);

  useEffect(() => {
    unsubRef.current?.();
    unsubRef.current = null;
    setBusiness(null);

    if (!activeId) return;

    const ref = doc(db, "businesses", activeId);

    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setBusiness(null);
        return;
      }

      const data = normalizeAdminBusiness(
        snap.data() as Record<string, unknown>,
        snap.id
      ) as TriageBusiness;

      setBusiness(data);

      setEditDraft({
        name: toEditValue(data.name ?? data.businessName),
        slug: toEditValue(data.slug),
        phone: toEditValue(data.phone ?? data.ownerPhone),
        website: toEditValue(data.website),
        addressLine: toEditValue(data.addressLine),
      });
    });

    unsubRef.current = unsub;

    return () => {
      unsub();
      unsubRef.current = null;
    };
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;

    const timer = setInterval(() => {
      extendClaim(activeId).catch(() => {});
    }, 60_000);

    return () => clearInterval(timer);
  }, [activeId]);

  const activeIdx = useMemo(() => {
    if (!activeId) return -1;
    return queue.findIndex((item) => item.id === activeId);
  }, [queue, activeId]);

  const activeQueueItem = useMemo(() => {
    return queue.find((item) => item.id === activeId) ?? null;
  }, [queue, activeId]);

  const clearPopups = useCallback(() => {
    setNoteMode(null);
    setNote("");
    setEditOpen(false);
  }, []);

  const openFullPreview = useCallback(() => {
    if (!activeId) return;

    router.push({
      pathname: "/admin/moderation/full/[id]",
      params: { id: activeId },
    });
  }, [activeId]);

  const claimNext = useCallback(async () => {
    try {
      setLoadingClaim(true);
      setBusyAction("next");

      if (activeId) {
        await releaseClaim(activeId).catch(() => {});
      }

      const nextId = await claimNextPending({ batch: 25 });
      clearPopups();

      if (!nextId) {
        setActiveId(null);
        Alert.alert("Queue empty", "No unclaimed pending businesses right now.");
        return;
      }

      setActiveId(nextId);
    } catch (error: unknown) {
      Alert.alert("Claim failed", getErrorMessage(error, "Could not claim next business."));
    } finally {
      setLoadingClaim(false);
      setBusyAction(null);
    }
  }, [activeId, clearPopups]);

  const next = useCallback(async () => {
    await claimNext();
  }, [claimNext]);

  const approve = useCallback(async () => {
    if (!activeId) return;

    try {
      setBusyAction("approve");
      await triageApprovePublish(activeId);
      await next();
    } catch (error: unknown) {
      Alert.alert("Approve failed", getErrorMessage(error, "Could not approve."));
      setBusyAction(null);
    }
  }, [activeId, next]);

  const sendReject = useCallback(
    async (reason: string) => {
      if (!activeId) return;

      try {
        setBusyAction("reject");
        await triageReject(activeId, reason);
        clearPopups();
        await next();
      } catch (error: unknown) {
        Alert.alert("Reject failed", getErrorMessage(error, "Could not reject."));
        setBusyAction(null);
      }
    },
    [activeId, clearPopups, next]
  );

  const sendChanges = useCallback(
    async (message: string) => {
      if (!activeId) return;

      try {
        setBusyAction("changes");
        await triageRequestChanges(activeId, message);
        clearPopups();
        await next();
      } catch (error: unknown) {
        Alert.alert("Request failed", getErrorMessage(error, "Could not request changes."));
        setBusyAction(null);
      }
    },
    [activeId, clearPopups, next]
  );

  const saveMinor = useCallback(async () => {
    if (!activeId || !business) return;

    const patch: Partial<MinorEditDraft> = {};
    const keys: (keyof MinorEditDraft)[] = [
      "name",
      "slug",
      "phone",
      "website",
      "addressLine",
    ];

    for (const key of keys) {
      const nextValue = toEditValue(editDraft[key]);
      const previousValue = toEditValue(business[key]);

      if (nextValue !== previousValue) {
        patch[key] = nextValue;
      }
    }

    if (Object.keys(patch).length === 0) {
      setEditOpen(false);
      return;
    }

    try {
      setBusyAction("edit");

      await triageEditMinor(
        activeId,
        patch as Parameters<typeof triageEditMinor>[1]
      );

      setEditOpen(false);
      await next();
    } catch (error: unknown) {
      Alert.alert("Edit failed", getErrorMessage(error, "Could not save edits."));
      setBusyAction(null);
    }
  }, [activeId, business, editDraft, next]);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const onKey = (event: KeyboardEvent) => {
      if (noteMode || editOpen) return;
      if (!activeId) return;

      const target = event.target as
        | (HTMLElement & { isContentEditable?: boolean })
        | null;

      const tag = target?.tagName?.toLowerCase();
      const isTyping =
        tag === "input" || tag === "textarea" || target?.isContentEditable;

      if (isTyping) return;

      const key = event.key.toLowerCase();

      if (key === "a") approve();
      if (key === "n") next();
      if (key === "f") openFullPreview();
      if (key === "e") setEditOpen(true);
      if (key === "r") setNoteMode("reject");
      if (key === "c") setNoteMode("changes");
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeId, approve, editOpen, next, noteMode, openFullPreview]);

  const renderQueueRow = ({ item }: { item: AdminBusiness }) => {
    const q = item as QueueBusiness;
    const selected = q.id === activeId;

    const queueLocation =
      [q.areaName, q.cityName, q.countyName]
        .filter((value) => hasText(value))
        .join(", ") || "Location not set";
    const queueCategoryText = getAdminBusinessCategoryLabel(q);
    const queueSubcategoryText = getAdminBusinessSubcategoryLabel(q);
    const queueCategoryLabel = queueCategoryText || "Not set";
    const queueSubcategoryLabel = queueSubcategoryText || "Not set";
    const showQueueCategoryOwnerSuggested = shouldShowAdminOwnerSuggestedLabel(
      queueCategoryText,
      q.categoryStatus,
      q.isCategoryOwnerSuggested
    );
    const showQueueSubcategoryOwnerSuggested =
      shouldShowAdminOwnerSuggestedLabel(
        queueSubcategoryText,
        q.subcategoryStatus,
        q.isSubcategoryOwnerSuggested
      );

    return (
      <Pressable
        onPress={() => setActiveId(q.id)}
        style={[styles.queueRow, selected && styles.queueRowSelected]}
      >
        <View style={styles.queueRowTop}>
          <Text style={styles.queueName} numberOfLines={1}>
            {q.name ?? q.businessName ?? "Unnamed business"}
          </Text>

          <View style={[styles.statusPill, selected && styles.statusPillSelected]}>
            <Text
              style={[
                styles.statusPillText,
                selected && styles.statusPillTextSelected,
              ]}
            >
              {q.status ?? "pending"}
            </Text>
          </View>
        </View>

        <Text style={styles.queueMeta} numberOfLines={1}>
          {q.ownerId ?? "—"}
        </Text>

        <View style={styles.queueTaxonomyBlock}>
          <View style={styles.queueTaxonomyRow}>
            <Text
              style={[styles.queueMetaMuted, styles.queueTaxonomyText]}
              numberOfLines={1}
            >
              Category: {queueCategoryLabel}
            </Text>
            {showQueueCategoryOwnerSuggested ? (
              <View style={styles.ownerSuggestedBadge}>
                <Text style={styles.ownerSuggestedText}>Owner suggested</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.queueTaxonomyRow}>
            <Text
              style={[styles.queueMetaMuted, styles.queueTaxonomyText]}
              numberOfLines={1}
            >
              Subcategory: {queueSubcategoryLabel}
            </Text>
            {showQueueSubcategoryOwnerSuggested ? (
              <View style={styles.ownerSuggestedBadge}>
                <Text style={styles.ownerSuggestedText}>Owner suggested</Text>
              </View>
            ) : null}
          </View>
        </View>

        <Text style={styles.queueMetaMuted} numberOfLines={1}>
          {queueLocation}
        </Text>
      </Pressable>
    );
  };

  const locationLine =
    business?.location?.areaName ||
    business?.areaName ||
    business?.location?.cityName ||
    business?.cityName ||
    business?.location?.countyName ||
    business?.countyName ||
    "—";

  const categoryText = getAdminBusinessCategoryLabel(business);
  const subcategoryText = getAdminBusinessSubcategoryLabel(business);
  const taxonomyLine =
    [categoryText, subcategoryText]
      .filter((value) => hasText(value))
      .join(" > ") || "—";
  const categoryOwnerSuggested = shouldShowAdminOwnerSuggestedLabel(
    categoryText,
    business?.categoryStatus,
    business?.isCategoryOwnerSuggested
  );
  const subcategoryOwnerSuggested = shouldShowAdminOwnerSuggestedLabel(
    subcategoryText,
    business?.subcategoryStatus,
    business?.isSubcategoryOwnerSuggested
  );

  const hasLogo = hasText(business?.logoUrl);
  const hasWebsite = hasText(business?.website);
  const hasPhone = hasText(business?.phone) || hasText(business?.ownerPhone);
  const hasAddress = hasText(business?.addressLine);
  const hasCategory = taxonomyLine !== "—";
  const hasLocation = locationLine !== "—";

  const mediaCount = getBusinessPhotoCount(business);

  return (
    <View style={styles.page}>
      <View style={styles.body}>
        <View style={[styles.leftCol, compact && styles.leftColCompact]}>
          <View style={styles.leftTopBar}>
            <View style={styles.leftTopTextWrap}>
              <Text style={styles.leftTopTitle}>Queue</Text>
              <Text style={styles.shortcutText}>
                A approve • C changes • R reject • E edit • F full • N next
              </Text>
            </View>

            <Pressable
              style={[styles.claimBtn, loadingClaim && styles.disabledBtn]}
              onPress={claimNext}
              disabled={loadingClaim}
            >
              <Text style={styles.claimBtnText}>
                {loadingClaim ? "Claiming…" : "Claim next"}
              </Text>
            </Pressable>
          </View>

          <View style={styles.leftQueueCard}>
            <View style={styles.leftHeader}>
              <Text style={styles.panelTitle}>Pending (unclaimed)</Text>
              <View style={styles.queueCountPill}>
                <Text style={styles.queueCountText}>{queue.length}</Text>
              </View>
            </View>

            <FlatList
              data={queue}
              keyExtractor={(item) => item.id}
              renderItem={renderQueueRow}
              ItemSeparatorComponent={() => <View style={styles.sep} />}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.queueListContent}
            />
          </View>
        </View>

        <View style={[styles.middleCol, compact && styles.middleColCompact]}>
          {!activeId ? (
            <View style={styles.empty}>
              <Text style={styles.h2}>No business selected</Text>
              <Text style={styles.sub}>Click “Claim next” to start moderating.</Text>
            </View>
          ) : !business ? (
            <View style={styles.empty}>
              <Text style={styles.h2}>Loading…</Text>
            </View>
          ) : (
            <ScrollView
              style={styles.middleScroll}
              contentContainerStyle={styles.middleScrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.actionPanel}>
                <Text style={styles.actionPanelTitle} numberOfLines={1}>
                  {business.name ?? business.businessName ?? "Business"}
                </Text>

                <Text style={styles.actionPanelMeta} numberOfLines={2}>
                  {taxonomyLine} • {locationLine}
                </Text>

                {categoryOwnerSuggested || subcategoryOwnerSuggested ? (
                  <View style={styles.ownerSuggestedRow}>
                    {categoryOwnerSuggested ? (
                      <View style={styles.ownerSuggestedBadge}>
                        <Text style={styles.ownerSuggestedText}>
                          Category: Owner suggested
                        </Text>
                      </View>
                    ) : null}

                    {subcategoryOwnerSuggested ? (
                      <View style={styles.ownerSuggestedBadge}>
                        <Text style={styles.ownerSuggestedText}>
                          Subcategory: Owner suggested
                        </Text>
                      </View>
                    ) : null}
                  </View>
                ) : null}

                <Text style={styles.actionPanelOwner} numberOfLines={1}>
                  Owner: {business.ownerId ?? "—"}
                </Text>

                <View style={styles.actionGrid}>
                  <Pressable
                    style={[
                      styles.gridBtn,
                      styles.gridBtnApprove,
                      busyAction === "approve" && styles.disabledBtn,
                    ]}
                    onPress={approve}
                    disabled={!!busyAction}
                  >
                    <Text style={styles.gridBtnPrimaryText}>
                      {busyAction === "approve" ? "Approving…" : "Approve"}
                    </Text>
                  </Pressable>

                  <Pressable
                    style={[styles.gridBtn, styles.gridBtnChanges]}
                    onPress={() => setNoteMode("changes")}
                    disabled={!!busyAction}
                  >
                    <Text style={styles.gridBtnWarnText}>Request changes</Text>
                  </Pressable>

                  <Pressable
                    style={[
                      styles.gridBtn,
                      styles.gridBtnReject,
                      busyAction === "reject" && styles.disabledBtn,
                    ]}
                    onPress={() => setNoteMode("reject")}
                    disabled={!!busyAction}
                  >
                    <Text style={styles.gridBtnPrimaryText}>Reject</Text>
                  </Pressable>

                  <Pressable
                    style={[styles.gridBtn, styles.gridBtnGhost]}
                    onPress={() => setEditOpen(true)}
                    disabled={!!busyAction}
                  >
                    <Text style={styles.gridBtnGhostText}>Minor edit</Text>
                  </Pressable>

                  <Pressable
                    style={[styles.gridBtn, styles.gridBtnGhost]}
                    onPress={openFullPreview}
                    disabled={!!busyAction}
                  >
                    <Text style={styles.gridBtnGhostText}>Full preview</Text>
                  </Pressable>

                  <Pressable
                    style={[styles.gridBtn, styles.gridBtnGhost]}
                    onPress={next}
                    disabled={!!busyAction}
                  >
                    <Text style={styles.gridBtnGhostText}>
                      {busyAction === "next" ? "Loading…" : "Next"}
                    </Text>
                  </Pressable>
                </View>
              </View>

              <View style={styles.infoCard}>
                <Text style={styles.cardTitle}>Quality checks</Text>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Logo</Text>
                  <Text style={hasLogo ? styles.okText : styles.warnText}>
                    {hasLogo ? "OK" : "Missing"}
                  </Text>
                </View>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Phone</Text>
                  <Text style={hasPhone ? styles.okText : styles.warnText}>
                    {hasPhone ? "OK" : "Missing"}
                  </Text>
                </View>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Website</Text>
                  <Text style={hasWebsite ? styles.okText : styles.warnText}>
                    {hasWebsite ? "OK" : "Missing"}
                  </Text>
                </View>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Address</Text>
                  <Text style={hasAddress ? styles.okText : styles.warnText}>
                    {hasAddress ? "OK" : "Missing"}
                  </Text>
                </View>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Category</Text>
                  <Text style={hasCategory ? styles.okText : styles.warnText}>
                    {hasCategory ? "OK" : "Missing"}
                  </Text>
                </View>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Location</Text>
                  <Text style={hasLocation ? styles.okText : styles.warnText}>
                    {hasLocation ? "OK" : "Missing"}
                  </Text>
                </View>

                <View style={styles.checkRow}>
                  <Text style={styles.checkLabel}>Images</Text>
                  <Text style={mediaCount > 0 ? styles.okText : styles.warnText}>
                    {mediaCount > 0 ? `${mediaCount} found` : "None"}
                  </Text>
                </View>
              </View>

              {hasText(business.rejectedReason) && (
                <View style={styles.noteCard}>
                  <Text style={styles.noteTitle}>Rejected reason</Text>
                  <Text style={styles.noteText}>{business.rejectedReason}</Text>
                </View>
              )}

              {hasText(business.changesRequestMessage) && (
                <View style={styles.noteCard}>
                  <Text style={styles.noteTitle}>Changes requested</Text>
                  <Text style={styles.noteText}>
                    {business.changesRequestMessage}
                  </Text>
                </View>
              )}

              {activeQueueItem && (
                <View style={styles.infoCard}>
                  <Text style={styles.cardTitle}>Queue context</Text>

                  <View style={styles.kvRow}>
                    <Text style={styles.k}>Queue</Text>
                    <Text style={styles.v}>
                      {queue.length
                        ? `${Math.max(0, activeIdx + 1)} / ${queue.length}`
                        : "—"}
                    </Text>
                  </View>

                  <View style={styles.kvRow}>
                    <Text style={styles.k}>Owner</Text>
                    <Text style={styles.v} numberOfLines={1}>
                      {(activeQueueItem as QueueBusiness).ownerId ?? "—"}
                    </Text>
                  </View>

                  <View style={styles.kvRow}>
                    <Text style={styles.k}>Status</Text>
                    <Text style={styles.v} numberOfLines={1}>
                      {(activeQueueItem as QueueBusiness).status ?? "pending"}
                    </Text>
                  </View>
                </View>
              )}
            </ScrollView>
          )}
        </View>

        <View style={styles.rightCol}>
          {!activeId ? (
            <View style={styles.empty}>
              <Text style={styles.h2}>No preview</Text>
              <Text style={styles.sub}>Select or claim a business to preview it here.</Text>
            </View>
          ) : !business ? (
            <View style={styles.empty}>
              <Text style={styles.h2}>Loading preview…</Text>
            </View>
          ) : (
            <View style={styles.previewPane}>
              <View style={styles.previewCardShell}>
                <BusinessCardResponsive business={business as unknown as Business} />
              </View>
            </View>
          )}
        </View>
      </View>

      <Modal visible={noteMode !== null} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {noteMode === "reject" ? "Reject business" : "Request changes"}
            </Text>

            <Text style={styles.modalSub}>Tap templates or write your own message.</Text>

            <View style={styles.templateWrap}>
              {(noteMode === "reject" ? REJECT_TEMPLATES : CHANGE_TEMPLATES).map(
                (template) => (
                  <Pressable
                    key={template}
                    style={styles.templateChip}
                    onPress={() =>
                      setNote((previous) =>
                        previous ? `${previous}\n• ${template}` : `• ${template}`
                      )
                    }
                  >
                    <Text style={styles.templateText}>{template}</Text>
                  </Pressable>
                )
              )}
            </View>

            <TextInput
              value={note}
              onChangeText={setNote}
              placeholder={
                noteMode === "reject" ? "Reason…" : "Tell owner what to fix…"
              }
              style={styles.textarea}
              multiline
            />

            <View style={styles.modalRow}>
              <Pressable
                style={styles.modalGhostBtn}
                onPress={() => {
                  setNoteMode(null);
                  setNote("");
                }}
              >
                <Text style={styles.modalGhostText}>Cancel</Text>
              </Pressable>

              <Pressable
                style={[
                  styles.modalPrimaryBtn,
                  busyAction !== null && styles.disabledBtn,
                ]}
                disabled={busyAction !== null}
                onPress={() => {
                  const message = note.trim();

                  if (!message) {
                    Alert.alert("Message required", "Please add a short note.");
                    return;
                  }

                  if (noteMode === "reject") {
                    sendReject(message);
                  } else {
                    sendChanges(message);
                  }
                }}
              >
                <Text style={styles.modalPrimaryText}>
                  {busyAction === "reject" || busyAction === "changes"
                    ? "Sending…"
                    : "Send"}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={editOpen} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Edit minor errors</Text>
            <Text style={styles.modalSub}>Keep edits small: typos and formatting only.</Text>

            <Text style={styles.label}>Name</Text>
            <TextInput
              value={editDraft.name ?? ""}
              onChangeText={(text) => setEditDraft((previous) => ({ ...previous, name: text }))}
              style={styles.input}
            />

            <Text style={styles.label}>Slug</Text>
            <TextInput
              value={editDraft.slug ?? ""}
              onChangeText={(text) => setEditDraft((previous) => ({ ...previous, slug: text }))}
              style={styles.input}
              autoCapitalize="none"
            />

            <Text style={styles.label}>Phone</Text>
            <TextInput
              value={editDraft.phone ?? ""}
              onChangeText={(text) => setEditDraft((previous) => ({ ...previous, phone: text }))}
              style={styles.input}
              keyboardType="phone-pad"
            />

            <Text style={styles.label}>Website</Text>
            <TextInput
              value={editDraft.website ?? ""}
              onChangeText={(text) =>
                setEditDraft((previous) => ({ ...previous, website: text }))
              }
              style={styles.input}
              autoCapitalize="none"
            />

            <Text style={styles.label}>Address line</Text>
            <TextInput
              value={editDraft.addressLine ?? ""}
              onChangeText={(text) =>
                setEditDraft((previous) => ({ ...previous, addressLine: text }))
              }
              style={styles.input}
            />

            <View style={styles.modalRow}>
              <Pressable style={styles.modalGhostBtn} onPress={() => setEditOpen(false)}>
                <Text style={styles.modalGhostText}>Cancel</Text>
              </Pressable>

              <Pressable
                style={[
                  styles.modalPrimaryBtn,
                  busyAction === "edit" && styles.disabledBtn,
                ]}
                onPress={saveMinor}
                disabled={busyAction === "edit"}
              >
                <Text style={styles.modalPrimaryText}>
                  {busyAction === "edit" ? "Saving…" : "Save"}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#0b0d10",
    padding: 12,
  },

  body: {
    flex: 1,
    flexDirection: "row",
    gap: 12,
    minHeight: 0,
  },

  leftCol: {
    width: 320,
    minHeight: 0,
    gap: 12,
  },
  leftColCompact: {
    width: 290,
  },

  leftTopBar: {
    borderWidth: 1,
    borderColor: "#20252c",
    backgroundColor: "#11161d",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 10,
  },
  leftTopTextWrap: {
    gap: 4,
  },
  leftTopTitle: {
    color: "#f8fafc",
    fontSize: 18,
    fontWeight: "800",
  },
  shortcutText: {
    color: "#94a3b8",
    fontSize: 12,
  },
  claimBtn: {
    backgroundColor: "#2563eb",
    paddingVertical: 11,
    paddingHorizontal: 14,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  claimBtnText: {
    color: "#fff",
    fontWeight: "800",
  },

  leftQueueCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#20252c",
    borderRadius: 16,
    backgroundColor: "#11161d",
    padding: 10,
    minHeight: 0,
  },
  leftHeader: {
    paddingHorizontal: 4,
    paddingBottom: 8,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#1f2937",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  panelTitle: {
    color: "#f8fafc",
    fontWeight: "800",
    fontSize: 14,
  },
  queueCountPill: {
    minWidth: 28,
    height: 28,
    borderRadius: 999,
    backgroundColor: "#13202f",
    borderWidth: 1,
    borderColor: "#2d6cdf",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  queueCountText: {
    color: "#bfdbfe",
    fontSize: 12,
    fontWeight: "800",
  },
  queueListContent: {
    paddingBottom: 4,
  },
  sep: {
    height: 8,
  },

  queueRow: {
    borderRadius: 14,
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: "#0d1319",
    borderWidth: 1,
    borderColor: "#18202a",
    gap: 4,
  },
  queueRowSelected: {
    backgroundColor: "#13202f",
    borderColor: "#2d6cdf",
  },
  queueRowTop: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  queueName: {
    flex: 1,
    color: "#f8fafc",
    fontWeight: "800",
    fontSize: 13,
  },
  queueMeta: {
    color: "#cbd5e1",
    fontSize: 12,
  },
  queueMetaMuted: {
    color: "#7c8aa0",
    fontSize: 11,
  },
  queueTaxonomyBlock: {
    gap: 4,
  },
  queueTaxonomyRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  queueTaxonomyText: {
    flexShrink: 1,
  },
  ownerSuggestedRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  ownerSuggestedBadge: {
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: "#D97706",
    backgroundColor: "#2A1D07",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  ownerSuggestedText: {
    color: "#FCD34D",
    fontSize: 10,
    fontWeight: "900",
    textTransform: "uppercase",
  },
  statusPill: {
    borderWidth: 1,
    borderColor: "#1f7a42",
    backgroundColor: "#0f2b1c",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  statusPillSelected: {
    borderColor: "#60a5fa",
    backgroundColor: "#163152",
  },
  statusPillText: {
    color: "#86efac",
    fontSize: 10,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  statusPillTextSelected: {
    color: "#bfdbfe",
  },

  middleCol: {
    width: 360,
    minHeight: 0,
  },
  middleColCompact: {
    width: 320,
  },
  middleScroll: {
    flex: 1,
  },
  middleScrollContent: {
    gap: 12,
    paddingBottom: 4,
  },

  actionPanel: {
    borderWidth: 1,
    borderColor: "#20252c",
    backgroundColor: "#11161d",
    borderRadius: 16,
    padding: 12,
    gap: 8,
  },
  actionPanelTitle: {
    color: "#f8fafc",
    fontSize: 16,
    fontWeight: "800",
  },
  actionPanelMeta: {
    color: "#cbd5e1",
    fontSize: 12,
  },
  actionPanelOwner: {
    color: "#7c8aa0",
    fontSize: 12,
    marginBottom: 2,
  },

  actionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  gridBtn: {
    width: "31.5%",
    minHeight: 40,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  gridBtnApprove: {
    backgroundColor: "#16a34a",
  },
  gridBtnChanges: {
    backgroundColor: "#f59e0b",
  },
  gridBtnReject: {
    backgroundColor: "#dc2626",
  },
  gridBtnGhost: {
    backgroundColor: "#111827",
    borderWidth: 1,
    borderColor: "#334155",
  },
  gridBtnPrimaryText: {
    color: "#fff",
    fontWeight: "800",
    fontSize: 12,
    textAlign: "center",
  },
  gridBtnWarnText: {
    color: "#111827",
    fontWeight: "800",
    fontSize: 12,
    textAlign: "center",
  },
  gridBtnGhostText: {
    color: "#e5e7eb",
    fontWeight: "800",
    fontSize: 12,
    textAlign: "center",
  },

  infoCard: {
    borderWidth: 1,
    borderColor: "#20252c",
    backgroundColor: "#11161d",
    borderRadius: 16,
    padding: 12,
    gap: 10,
  },
  cardTitle: {
    color: "#f8fafc",
    fontWeight: "800",
    fontSize: 14,
  },
  kvRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },
  k: {
    width: 74,
    color: "#94a3b8",
    fontSize: 12,
    fontWeight: "700",
  },
  v: {
    flex: 1,
    color: "#f8fafc",
    fontSize: 12,
    fontWeight: "600",
  },

  checkRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  checkLabel: {
    color: "#cbd5e1",
    fontSize: 12,
    fontWeight: "600",
  },
  okText: {
    color: "#4ade80",
    fontSize: 12,
    fontWeight: "800",
  },
  warnText: {
    color: "#fbbf24",
    fontSize: 12,
    fontWeight: "800",
  },

  noteCard: {
    borderWidth: 1,
    borderColor: "#334155",
    backgroundColor: "#0f172a",
    borderRadius: 16,
    padding: 12,
    gap: 6,
  },
  noteTitle: {
    color: "#f8fafc",
    fontWeight: "800",
    fontSize: 13,
  },
  noteText: {
    color: "#cbd5e1",
    fontSize: 12,
    lineHeight: 18,
  },

  rightCol: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    borderWidth: 1,
    borderColor: "#20252c",
    borderRadius: 16,
    backgroundColor: "#11161d",
    padding: 10,
  },
  previewPane: {
    flex: 1,
    minHeight: 0,
  },
  previewCardShell: {
    flex: 1,
    minHeight: 0,
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: "#111827",
    borderWidth: 1,
    borderColor: "#1f2937",
    padding: 8,
  },

  empty: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
  },
  h2: {
    color: "#f8fafc",
    fontSize: 18,
    fontWeight: "800",
  },
  sub: {
    color: "#94a3b8",
    fontSize: 12,
    textAlign: "center",
  },

  disabledBtn: {
    opacity: 0.65,
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.62)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 760,
    backgroundColor: "#ffffff",
    borderRadius: 18,
    padding: 16,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: "900",
    color: "#0f172a",
  },
  modalSub: {
    color: "#475569",
    marginTop: 4,
    marginBottom: 12,
  },
  templateWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  templateChip: {
    borderWidth: 1,
    borderColor: "#dbe2ea",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#f8fafc",
  },
  templateText: {
    fontWeight: "700",
    fontSize: 12,
    color: "#0f172a",
  },
  textarea: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 12,
    padding: 12,
    height: 120,
    textAlignVertical: "top",
    color: "#0f172a",
    backgroundColor: "#fff",
  },
  modalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 14,
  },
  modalGhostBtn: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: "#fff",
  },
  modalGhostText: {
    color: "#0f172a",
    fontWeight: "800",
  },
  modalPrimaryBtn: {
    backgroundColor: "#111827",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
  },
  modalPrimaryText: {
    color: "#fff",
    fontWeight: "800",
  },

  label: {
    marginTop: 8,
    color: "#475569",
    fontSize: 12,
    fontWeight: "700",
  },
  input: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 12,
    padding: 12,
    marginTop: 6,
    color: "#0f172a",
    backgroundColor: "#fff",
  },
});
